import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  HydraulicWallForm,
  HydraulicWallListMeta,
  HydraulicWallRow,
  HydraulicWallUpdatePayload,
} from "../types";
import { initialHydraulicWallForm, initialState } from "./initialState";

export const sliceKey = "hydraulicWallState";

type HydraulicWallListSuccessPayload = {
  data: HydraulicWallRow[];
  meta: HydraulicWallListMeta;
};

const hydraulicWallSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getHydraulicWallList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getHydraulicWallListSuccess(
      state,
      action: PayloadAction<HydraulicWallListSuccessPayload>
    ) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getHydraulicWallListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<HydraulicWallListSuccessPayload>
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialHydraulicWallForm;
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof HydraulicWallForm;
        value: string;
      }>
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    addHydraulicWall(state) {
      state.saving = true;
      state.error = null;
    },

    addHydraulicWallSuccess(state) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialHydraulicWallForm;
      state.error = null;
    },

    addHydraulicWallError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    updateHydraulicWall(
      state,
      _action: PayloadAction<HydraulicWallUpdatePayload>,
    ) {
      state.saving = true;
      state.error = null;
    },

    updateHydraulicWallSuccess(state) {
      state.saving = false;
      state.error = null;
    },

    updateHydraulicWallError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    deleteHydraulicWall(state, _action: PayloadAction<number>) {
      state.saving = true;
      state.error = null;
    },

    deleteHydraulicWallSuccess(state, action: PayloadAction<number>) {
      state.saving = false;
      state.error = null;
      state.list = state.list.filter(
        (item) => item.hydraulicTypeId !== action.payload,
      );
    },

    deleteHydraulicWallError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
  },
});

export const { actions: hydraulicWallActions, reducer } = hydraulicWallSlice;
